<template>
  <div class="base-signature-pad-wrapper">
    <label v-if="label" class="base-signature-pad-label">
      {{ label }}
      <span v-if="required" class="text-danger dark:text-danger-dark ml-0.5">*</span>
    </label>

    <div class="base-signature-pad-container" :class="{ 'has-error': error, 'has-value': hasValue }">
      <canvas
        ref="canvasEl"
        class="base-signature-pad-canvas"
        :width="width"
        :height="height"
        :class="{ 'has-error': error }"
      />
      <div class="base-signature-pad-overlay" :class="{ 'is-hidden': hasValue }">
        <span class="base-signature-pad-overlay-text">{{ placeholder }}</span>
      </div>
      <div class="base-signature-pad-controls">
        <button type="button" class="base-signature-pad-clear" @click="clear">
          <Eraser class="h-3.5 w-3.5" />
          Clear
        </button>
      </div>
    </div>

    <!-- Hint text -->
    <p v-if="hint && !error" class="base-signature-pad-hint">{{ hint }}</p>

    <!-- Error message -->
    <p v-if="error" class="base-signature-pad-error">{{ error }}</p>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch, nextTick } from 'vue'
import SignaturePad from 'signature_pad'
import { Eraser } from 'lucide-vue-next'

const props = withDefaults(defineProps<{
  modelValue?: string
  label?: string
  placeholder?: string
  required?: boolean
  error?: string
  hint?: string
  width?: number
  height?: number
  penColor?: string
  disabled?: boolean
}>(), {
  modelValue: '',
  placeholder: 'Sign here',
  required: false,
  width: 600,
  height: 200,
  penColor: '#111827',
  disabled: false
})

const emit = defineEmits<{
  'update:modelValue': [value: string]
}>()

const canvasEl = ref<HTMLCanvasElement | null>(null)
let pad: SignaturePad | null = null
let strokeEndHandler: (() => void) | null = null

const hasValue = ref(false)

function syncFromModel() {
  if (!pad || !canvasEl.value) return
  if (props.modelValue) {
    pad.fromDataURL(props.modelValue)
    hasValue.value = true
  } else {
    pad.clear()
    hasValue.value = false
  }
}

function initPad() {
  if (!canvasEl.value) return

  canvasEl.value.width = canvasEl.value.offsetWidth || props.width
  canvasEl.value.height = props.height
  canvasEl.value.style.height = `${props.height}px`

  pad = new SignaturePad(canvasEl.value, {
    minWidth: 0.5,
    maxWidth: 2.5,
    penColor: props.penColor,
    backgroundColor: 'rgb(255,255,255)',
  })
  strokeEndHandler = () => {
    if (!pad) return
    hasValue.value = !pad.isEmpty()
    emit('update:modelValue', pad.isEmpty() ? '' : pad.toDataURL())
  }
  pad.addEventListener('endStroke', strokeEndHandler)
  syncFromModel()
}

function clear() {
  if (!pad) return
  pad.clear()
  hasValue.value = false
  emit('update:modelValue', '')
}

watch(() => props.modelValue, (value) => {
  if (!pad) return
  const current = pad.isEmpty() ? '' : pad.toDataURL()
  if (current !== value) {
    syncFromModel()
  }
})

watch(() => props.penColor, (color) => {
  if (pad && color) {
    pad.penColor = color
  }
})

watch(() => props.disabled, (val) => {
  if (pad) pad.enabled = !val
})

onMounted(() => {
  nextTick(() => {
    requestAnimationFrame(() => {
      initPad()
    })
  })
})

onBeforeUnmount(() => {
  if (pad && strokeEndHandler) {
    pad.removeEventListener('endStroke', strokeEndHandler)
    strokeEndHandler = null
  }
  pad = null
})

defineExpose({
  clear,
  isEmpty: () => (pad ? pad.isEmpty() : true),
})
</script>

<style scoped>
.base-signature-pad-wrapper {
  @apply w-full;
}

.base-signature-pad-label {
  @apply block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5;
}

.base-signature-pad-container {
  @apply relative rounded-xl border border-gray-300 dark:border-gray-600 overflow-hidden
         bg-white dark:bg-gray-800
         transition-all duration-200;
}

.base-signature-pad-container:focus-within {
  @apply ring-2 ring-primary/40 border-primary
         dark:ring-primary-dark/40 dark:border-primary-dark;
}

.base-signature-pad-container.has-error {
  @apply border-danger dark:border-danger-dark
         focus:ring-danger/40 dark:focus:ring-danger-dark/40;
}

.base-signature-pad-canvas {
  @apply block touch-none cursor-crosshair;
  width: 100%;
}

.base-signature-pad-overlay {
  @apply absolute inset-0 flex items-center justify-center
         pointer-events-none text-gray-400 dark:text-gray-500 text-sm
         transition-opacity duration-200;
}

.base-signature-pad-overlay.is-hidden {
  @apply opacity-0;
}

.base-signature-pad-controls {
  @apply absolute bottom-2 right-2;
}

.base-signature-pad-clear {
  @apply inline-flex items-center gap-1 px-2.5 py-1 rounded-lg
         text-xs font-medium text-gray-500 dark:text-gray-400
         bg-gray-100/90 dark:bg-gray-700/90
         hover:text-gray-700 dark:hover:text-gray-200
         hover:bg-gray-200/90 dark:hover:bg-gray-600/90
         transition-colors duration-150
         opacity-0 pointer-events-none;
}

.base-signature-pad-container:hover .base-signature-pad-clear,
.base-signature-pad-clear:focus-visible {
  @apply opacity-100 pointer-events-auto;
}

.base-signature-pad-hint {
  @apply mt-1.5 text-xs text-gray-500 dark:text-gray-400;
}

.base-signature-pad-error {
  @apply mt-1.5 text-xs text-danger dark:text-danger-dark font-medium;
  animation: slideDown 0.2s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
